import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import {
    Row,
    Col,
    Card,
    Button,
    Form,
    Alert,
    Spinner,
    InputGroup
} from 'react-bootstrap';
import { CircularProgress } from '@mui/material';
import { Badge } from 'react-bootstrap';
import {
    ArrowBack,
    PlayArrow,
    Upload,
    FilePresent,
    Save,
    FormatListBulleted,
    TextFields,
    CheckCircleOutline,
    Notes,
    ShortText,
    Description as DescriptionIcon,
    Article,
    HistoryEdu,
    Add,
    Remove,
    CheckCircle,
    AddCircleOutline,
    RemoveCircleOutline
} from '@mui/icons-material';
import { generateQNAAPI, generateQnaPDF, getUploadedResorucesAPI, getConfigOrganization } from 'ServerAPI/serverAPI'; // Update API

import CommonHeader from 'layouts/dashboard/CommonHeader';
import { useToast } from 'context/toast-context/ToastProvider';
import UploadFile from 'components/courses/add-new-course/steps/UploadFile';
import { useResponsive } from 'components/ResponsiveDesign/ResponsiveDesign';
import courseIcon from '../../assets/images/Courses.svg';
import Multiselect from 'multiselect-react-dropdown';
import { DisplayGeneratedQnA } from "components/courses/add-new-course/steps/quiz-genaration/DisplayGeneratedQnA";
import { EditableQnADisplay } from "./EditableQnADisplay";
import AccaExamGeneration from "./AccaExamGeneration";
import { useFeaturePermissions, FEATURES } from "constants/FeaturePermissions";

const QAGeneration = () => {
    const [cookies] = useCookies(['userId', 'organizationId']);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { showToast } = useToast();
    const { isMobile, isTablet } = useResponsive();
    const { hasFeature } = useFeaturePermissions();
    const hasAccaFeature = hasFeature(FEATURES.ACCA_FEATURES);
    const { userId: routeUserId, generatedContentId: initialGeneratedContentId } = location.state || {};
    const userId = routeUserId || cookies.userId; // Prefer route, fallback to cookie
    const { qaId } = useParams();
    const isViewingMode = !!qaId;

    const [formData, setFormData] = useState({
        title: '',
        domain: '',
        department: '',
    });

    const [isAccaExam, setIsAccaExam] = useState(false);

    const [selectedResourceFile, setSelectedResourceFile] = useState(null); // From dropdown
    const [uploadedFileInfo, setUploadedFileInfo] = useState(null); // From upload
    const [availableFiles, setAvailableFiles] = useState([]); // For dropdown
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationError, setGenerationError] = useState(null);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [difficultyLevel, setDifficultyLevel] = useState("medium");
    const [questionConfigs, setQuestionConfigs] = useState({
        mcq: { enabled: true, count: 1, taxonomy: 'Remember', marks: 1, difficulty: 'easy' },
        fill_in_the_blanks: { enabled: false, count: 1, taxonomy: 'Understand', marks: 1, difficulty: 'easy' },
        true_or_false: { enabled: false, count: 1, taxonomy: 'Understand', marks: 1, difficulty: 'easy' },
        very_short_answer: { enabled: false, count: 1, taxonomy: 'Remember', marks: 2, difficulty: 'easy' },
        short_answer: { enabled: false, count: 1, taxonomy: 'Understand', marks: 3, difficulty: 'easy' },
        medium_answer: { enabled: false, count: 1, taxonomy: 'Apply', marks: 5, difficulty: 'easy' },
        long_answer: { enabled: false, count: 1, taxonomy: 'Analyze', marks: 10, difficulty: 'easy' },
        essay_answer: { enabled: false, count: 1, taxonomy: 'Evaluate', marks: 15, difficulty: 'easy' },
    });

    const [additionalContext, setAdditionalContext] = useState("");
    const [quizGenerationResponseData, setQuizGenerationResponseData] = useState(null);
    const [transformedData, setTransformedData] = useState([]);
    const [isSavingPDF, setIsSavingPDF] = useState(false);
    const [editableQuestions, setEditableQuestions] = useState([]);

    const [orgConfig, setOrgConfig] = useState({
        departments: [],
        domains: []
    });
    const [isLoadingOrgConfig, setIsLoadingOrgConfig] = useState(false);
    // Fetch available files and org config on mount
    useEffect(() => {
        fetchAvailableResources();
        fetchOrgConfig();
    }, []);

    const fetchOrgConfig = async () => {
        try {
            setIsLoadingOrgConfig(true);
            const response = await getConfigOrganization(cookies.email, cookies.organizationId, userId);
            if (response.status === 200) {
                setOrgConfig({
                    departments: response.data.department || [],
                    domains: response.data.domain || []
                });
            }
        } catch (error) {
            console.error('Error fetching org config:', error);
        } finally {
            setIsLoadingOrgConfig(false);
        }
    };

    // Fetch only vectorized resources
    const fetchAvailableResources = async () => {
        try {
            setIsLoadingFiles(true);
            const response = await getUploadedResorucesAPI(userId);

            if (response.data && Array.isArray(response.data.resources)) {
                // Filter only vectorSpace: true
                const vectorizedFiles = response.data.resources
                    .filter(file => file.vectorSpace === true)
                    .map(file => ({
                        id: file.fileId,
                        name: file.contentFileName,
                        size: null, // optional: you can add size if backend provides
                        createdAt: file.createdDate?.$date
                    }));

                setAvailableFiles(vectorizedFiles);
            } else {
                setAvailableFiles([]);
            }
        } catch (error) {
            console.error('Error fetching resources:', error);
            showToast('Failed to load available resources', 'error');
            setAvailableFiles([]);
        } finally {
            setIsLoadingFiles(false);
        }
    };

    // === AUTO CALCULATE TOTAL QUESTIONS ===
    const totalQuestions = Object.values(questionConfigs).reduce((acc, config) => {
        return config.enabled ? acc + (Number(config.count) || 0) : acc;
    }, 0);

    // === HANDLERS FOR CONFIGURATION ===
    const handleConfigChange = (type, field, value) => {
        setQuestionConfigs(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [field]: value
            }
        }));
    };

    const incrementConfig = (type, field, max = 50) => {
        const currentValue = parseInt(questionConfigs[type][field]) || 0;
        if (currentValue < max) {
            handleConfigChange(type, field, currentValue + 1);
        }
    };

    const decrementConfig = (type, field, min = 0) => {
        const currentValue = parseInt(questionConfigs[type][field]) || 0;
        if (currentValue > min) {
            handleConfigChange(type, field, currentValue - 1);
        }
    };

    const handleTypeToggle = (type) => {
        setQuestionConfigs(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                enabled: !prev[type].enabled
            }
        }));
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setGenerationError(null);
    };

    const handleResourceSelect = (e) => {
        const fileId = e.target.value;
        const file = availableFiles.find(f => f.id === fileId);
        setSelectedResourceFile(file || null);
        setUploadedFileInfo(null); // Clear upload
        setGenerationError(null);
    };

    const handleFileUploadChange = (file) => {
        setUploadedFileInfo(file.target.files[0]);
        setSelectedResourceFile(null); // Clear dropdown
        setGenerationError(null);
    };

    const validateForm = () => {
        if (!formData.title.trim()) {
            setGenerationError('Title is required');
            return false;
        }
        if (!formData.domain.trim()) {
            setGenerationError('Domain is required');
            return false;
        }
        if (!formData.department.trim()) {
            setGenerationError('Department is required');
            return false;
        }
        if (!selectedResourceFile && !uploadedFileInfo) {
            setGenerationError('Please select a file');
            return false;
        }

        const hasEnabledTypes = Object.values(questionConfigs).some(config => config.enabled && config.count > 0);
        if (!hasEnabledTypes) {
            setGenerationError('Please enable at least one question type with a count greater than 0');
            return false;
        }
        return true;
    };

    // === ALSO ADD THIS STATE FOR questionsDetails to utilise DisplayGeneratedQnA component===
    const [questionsDetails, setQuestionsDetails] = useState({
        passingPercentage: 70,
        finalAssessment: false,
        quizTimer: 30,
        vectorisedFile: "",
        difficultyLevel: ["medium"],
    });
    //===== timer state for DisplayGeneratedQnA component ======
    const [quizTimer, setQuizTimer] = useState(30);


    const handleGenerateQA = async () => {
        if (!validateForm()) return;

        try {
            setIsGenerating(true);
            setGenerationError(null);

            const mandatoryQuestions = Object.entries(questionConfigs)
                .filter(([_, config]) => config.enabled && config.count > 0)
                .map(([type, config]) => ({
                    question_type: type,
                    total_questions: Number(config.count),
                    bloom_taxonomy: config.taxonomy,
                    marks_per_question: Number(config.marks),
                    // difficulty_level: config.difficulty
                }));

            const payload = {
                pdf_filename: selectedResourceFile?.name || uploadedFileInfo?.name,
                org_id: cookies.organizationId || "781bb6da-7908-41e4-86ab-8676bc085c56", // Fallback for testing
                domain: formData.domain,
                department: formData.department,
                mandatory: mandatoryQuestions,
                title: formData.title
            };

            // === CALL THE CORRECT API ===
            const response = await generateQNAAPI(payload);

            // Map responses (this might need adjustments based on actual backend response for new types)
            const allQuestions = [];
            const responseData = response.data || {};

            // Helper to process questions from different keys in response
            const processQuestions = (questions, type) => {
                if (!Array.isArray(questions)) return;
                questions.forEach((q) => {
                    allQuestions.push({
                        id: allQuestions.length + 1,
                        type: type,
                        question: q.question,
                        options: q.options ? q.options.map(opt => typeof opt === 'object' ? opt.text : opt) : undefined,
                        correctAnswer: q.correct_answer || q.answer,
                        explanation: q.explanation || "",
                        reference: q.reference || "",
                        taxonomy: q.taxonomy_level || q.taxonomy || "",
                        courseOutcome: q.course_outcome || "",
                        chunkId: q.chunk_id || "",
                        topics: q.topics_covered || [],
                        marks: q.marks || 1,
                        distractorRationale: q.distractor_rationale || {},
                        rubric: Array.isArray(q.rubric) ? q.rubric : []
                    });
                });
            };

            const actualData = responseData.data || responseData;

            processQuestions(actualData.mcq || actualData.mcq_questions, 'mcq');
            processQuestions(actualData.fill_in_the_blanks || actualData.fillup_questions, 'fill_in_the_blanks');
            processQuestions(actualData.true_or_false || actualData.trf_questions, 'true_or_false');
            processQuestions(actualData.very_short_answer || actualData.very_short_questions, 'very_short_answer');
            processQuestions(actualData.short_answer || actualData.short_questions, 'short_answer');
            processQuestions(actualData.medium_answer || actualData.medium_questions, 'medium_answer');
            processQuestions(actualData.long_answer || actualData.long_questions, 'long_answer');
            processQuestions(actualData.essay_answer || actualData.essay_questions, 'essay_answer');
            processQuestions(actualData.programming || actualData.programming_questions, 'programming');

            // Store for display
            setEditableQuestions(allQuestions);
            setTransformedData(allQuestions);
            setQuizGenerationResponseData(responseData);

            // Update questionsDetails
            setQuestionsDetails(prev => ({
                ...prev,
                vectorisedFile: selectedResourceFile?.name || uploadedFileInfo?.name || "",
                difficultyLevel: [difficultyLevel],
            }));

            showToast('Q&A generated successfully!', 'success');
        } catch (error) {
            console.error('Error generating Q&A:', error);
            setGenerationError(error.response?.data?.message || 'Failed to generate Q&A');
            showToast('Failed to generate Q&A', 'error');
        } finally {
            setIsGenerating(false);
        }
    };

    // NEW: Handle saving (both original save + PDF generation)
    const handleGeneratedQuizSubmit = async (editableData, passingPercentage, finalAssessment, quizTimer) => {
        try {
            setIsSavingPDF(true);

            // Update the editable questions
            setEditableQuestions(editableData);

            // 1. First, do whatever the original save was supposed to do
            const savePayload = {
                quizContent: editableData,
                passingPercentage: Number(passingPercentage) || 70,
                finalAssessment: finalAssessment || false,
                quizTimer: Number(quizTimer) || 30,
                additional_data: {
                    fileName: selectedResourceFile?.name || uploadedFileInfo?.name,
                    DifficultyLevel: formData.difficulty || "medium",
                    MCQ: Number(questionConfigs.mcq.count) || 0,
                    FIB: Number(questionConfigs.fill_in_the_blanks.count) || 0,
                    TF: Number(questionConfigs.true_or_false.count) || 0,
                }
            };


            // 2. Then generate PDF
            const pdfPayload = {
                question_count: totalQuestions,
                fileName: selectedResourceFile?.name || uploadedFileInfo?.name,
                userId: userId,
                title: formData.title,
                request_type: "save_db",
                questions: editableData.map(q => ({
                    question: q.question,
                    options: q.type === 'mcq' ? {
                        a: q.options?.[0] || "",
                        b: q.options?.[1] || "",
                        c: q.options?.[2] || "",
                        d: q.options?.[3] || ""
                    } : undefined,
                    correct_answer: q.correctAnswer,
                    explanation: q.explanation,
                    question_type: q.type,
                    answer: q.type === 'true_false' ? q.correctAnswer : q.correctAnswer, // Send as answer too
                    taxonomy_level: q.taxonomy,
                    difficulty_level: q.difficulty || formData.difficulty || "medium",
                    marks: q.marks,
                    course_outcome: q.courseOutcome,
                    topics_covered: q.topics,
                    chunk_id: q.chunkId,
                    distractor_rationale: q.distractorRationale,
                    rubric: q.rubric || []
                }))
            };

            const pdfResponse = await generateQnaPDF(pdfPayload);

            if (pdfResponse.data.success) {
                showToast('Questions saved and PDF generated successfully!', 'success');
                showToast('Success!', 'success');
                navigate('/content-generation-dashboard', { state: { activeTab: 'qna' } });
                console.log('PDF generated with ID:', pdfResponse.data.qnaId);
            } else {
                showToast('Questions saved but PDF generation failed', 'error');
            }
        } catch (error) {
            console.error('Error saving questions:', error);
            showToast('Failed to save questions', 'error');
        } finally {
            setIsSavingPDF(false);
        }
    };


    const handleBack = () => {
        navigate('/content-generation-dashboard');
    };
    const renderGenerationForm = () => {
        const BLOOM_TAXONOMY_OPTIONS = [
            'Remember',
            'Understand',
            'Apply',
            'Analyze',
            'Evaluate',
            'Create'
        ];

        const QUESTION_TYPE_LABELS = {
            mcq: { label: "MCQ", icon: <FormatListBulleted className="text-primary" /> },
            fill_in_the_blanks: { label: "Fill in the Blanks", icon: <TextFields className="text-primary" /> },
            true_or_false: { label: "True/False", icon: <CheckCircleOutline className="text-primary" /> },
            very_short_answer: { label: "Very Short Answer", icon: <Notes className="text-primary" /> },
            short_answer: { label: "Short Answer", icon: <ShortText className="text-primary" /> },
            medium_answer: { label: "Medium Answer", icon: <DescriptionIcon className="text-primary" /> },
            long_answer: { label: "Long Answer", icon: <Article className="text-primary" /> },
            essay_answer: { label: "Essay Answer", icon: <HistoryEdu className="text-primary" /> }
        };

        return (
            <div>
                <div className="mb-4 p-4 border rounded shadow-sm bg-white">
                    <h5 className="fw-bold mb-4 pb-2 border-bottom text-primary">General Information</h5>
                    <Row className="mb-4">
                        <Col md={12}>
                            <Form.Label className="fw-semibold">Q&A Title *</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., 'Python Basics Quiz' or 'Marketing Exam Q&A'"
                                value={formData.title}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                disabled={isGenerating}
                                maxLength={100}
                                className="bg-light border-0 py-2"
                            />
                            <Form.Text className="text-muted">
                                Enter a descriptive title (max 100 characters)
                            </Form.Text>
                        </Col>
                    </Row>

                    {hasAccaFeature && (
                        <Row className="mb-4">
                            <Col md={12}>
                                <Form.Check
                                    type="switch"
                                    id="acca-exam-checkbox"
                                    label={<span className="fw-bold text-primary ms-2" style={{fontSize: "1.1rem"}}>ACCA Exams Format</span>}
                                    checked={isAccaExam}
                                    onChange={(e) => setIsAccaExam(e.target.checked)}
                                    disabled={isGenerating}
                                />
                                <Form.Text className="text-muted ms-5">
                                    Enable this to generate a PDF conforming to the ACCA examination layout.
                                </Form.Text>
                            </Col>
                        </Row>
                    )}

                    {!(isAccaExam && hasAccaFeature) && (
                        <Row className="mb-0">
                            <Col md={6}>
                                <Form.Label className="fw-semibold">Domain *</Form.Label>
                                <Form.Select
                                    value={formData.domain}
                                    onChange={(e) => handleInputChange('domain', e.target.value)}
                                    disabled={isGenerating || isLoadingOrgConfig}
                                    className="bg-light border-0 py-2"
                                >
                                    <option value="">-- Select Domain --</option>
                                    {orgConfig.domains.map((dom, idx) => (
                                        <option key={idx} value={dom.title}>
                                            {dom.title}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={6}>
                                <Form.Label className="fw-semibold">Department *</Form.Label>
                                <Form.Select
                                    value={formData.department}
                                    onChange={(e) => handleInputChange('department', e.target.value)}
                                    disabled={isGenerating || isLoadingOrgConfig}
                                    className="bg-light border-0 py-2"
                                >
                                    <option value="">-- Select Department --</option>
                                    {orgConfig.departments.map((dept, idx) => (
                                        <option key={idx} value={dept.title}>
                                            {dept.title}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>
                    )}
                </div>

                <div className="mb-4 p-4 border rounded shadow-sm bg-white">
                    <h5 className="fw-bold mb-4 pb-2 border-bottom text-primary">Content Source</h5>
                    <div className="mb-0">
                        <Form.Label className="fw-semibold">
                            <FilePresent fontSize="small" className="me-2" />
                            Select from My Files *
                        </Form.Label>
                        <Form.Select
                            value={selectedResourceFile?.id || ''}
                            onChange={handleResourceSelect}
                            disabled={isGenerating || isLoadingFiles || !!uploadedFileInfo}
                            className="bg-light border-0 py-2 mb-2"
                        >
                            <option value="">-- Select a resource --</option>
                            {availableFiles.length > 0 ? (
                                availableFiles.map(file => (
                                    <option key={file.id} value={file.id}>
                                        {file.name}
                                    </option>
                                ))
                            ) : (
                                <option disabled>No resources found</option>
                            )}
                        </Form.Select>
                        {isLoadingFiles && (
                            <div className="mt-1">
                                <Spinner animation="border" size="sm" className="me-2 text-primary" />
                                <small className="text-muted">Loading files...</small>
                            </div>
                        )}
                        {selectedResourceFile ? (
                            <Alert variant="success" className="py-2 px-3 mt-2 border-0 bg-success-subtle d-inline-block">
                                <small className="fw-bold">Selected: {selectedResourceFile.name}</small>
                            </Alert>
                        ) : (
                            !isLoadingFiles && !uploadedFileInfo && (
                                <small className="text-danger fw-bold d-block mt-1">Please select a file</small>
                            )
                        )}
                    </div>
                </div>

                {isAccaExam && hasAccaFeature ? (
                    <AccaExamGeneration
                        organizationId={cookies.organizationId}
                        selectedFile={selectedResourceFile || uploadedFileInfo}
                    />
                ) : (
                    <>
                        {/* === QUESTION CONFIGURATION === */}
                        <div className="mb-4 p-4 border rounded shadow-sm bg-white" style={{ opacity: isGenerating ? 0.5 : 1, pointerEvents: isGenerating ? 'none' : 'auto', transition: 'opacity 0.3s ease' }}>
                            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                                <div>
                                    <h5 className="fw-bold mb-1 text-primary">Question Configuration</h5>
                                    <p className="text-muted small mb-0">Configure granular settings for each question type.</p>
                                </div>
                                <div className="text-end">
                                    <span className="badge bg-primary px-3 py-2 fs-6 shadow-sm">
                                        Total Questions: {totalQuestions}
                                    </span>
                                </div>
                            </div>

                            <div className="question-configuration-list">
                                <style>{`
                            .question-configuration-list {
                                display: flex;
                                flex-direction: column;
                                gap: 16px;
                            }
                            .q-config-row {
                                border: 1px solid #e2e8f0;
                                border-radius: 16px;
                                transition: all 0.3s ease;
                                background-color: #fff;
                                overflow: hidden;
                            }
                            .q-config-row.enabled {
                                border-color: #224b99;
                                box-shadow: 0 8px 20px rgba(34, 75, 153, 0.06);
                            }
                            .q-row-header {
                                padding: 18px 24px;
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                                cursor: pointer;
                                background-color: #fff;
                            }
                            .q-row-header:hover {
                                background-color: #f8faff;
                            }
                            .q-row-header .form-check-input {
                                width: 1.5rem;
                                height: 1.5rem;
                                cursor: pointer;
                                margin-top: 0;
                            }
                            .q-row-header .form-check-label {
                                font-weight: 600;
                                color: #1e293b;
                                margin-left: 12px;
                                cursor: pointer;
                                font-size: 1.1rem;
                                display: flex;
                                align-items: center;
                                gap: 12px;
                            }
                            .q-config-row.enabled .q-row-header .form-check-label {
                                color: #224b99;
                            }
                            .q-row-body {
                                padding: 24px;
                                border-top: 1px solid #f1f5f9;
                                background-color: #fcfdfe;
                                animation: slideIn 0.3s ease-out;
                            }
                            @keyframes slideIn {
                                from { opacity: 0; transform: translateY(-8px); }
                                to { opacity: 1; transform: translateY(0); }
                            }
                            .setting-group {
                                display: grid;
                                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                                gap: 24px;
                            }
                            .setting-item label {
                                color: #64748b;
                                font-size: 0.85rem;
                                text-transform: uppercase;
                                letter-spacing: 0.025em;
                            }
                            .setting-item .form-control, .setting-item .form-select {
                                border: 1.5px solid #e2e8f0;
                                border-radius: 8px;
                                padding: 8px 12px;
                            }
                            .setting-item .form-control:focus, .setting-item .form-select:focus {
                                border-color: #224b99;
                                box-shadow: 0 0 0 3px rgba(34, 75, 153, 0.1);
                            }
                        `}</style>

                                {Object.entries(questionConfigs).map(([type, config]) => (
                                    <div key={type} className={`q-config-row ${config.enabled ? 'enabled' : 'disabled'}`}>
                                        <div
                                            className="q-row-header"
                                            onClick={() => handleTypeToggle(type)}
                                        >
                                            <div className="d-flex align-items-center">
                                                <div className={`me-3 d-flex align-items-center justify-content-center rounded-circle ${config.enabled ? 'bg-primary text-white' : 'bg-light text-muted'}`} style={{ width: '32px', height: '32px', transition: 'all 0.2s' }}>
                                                    {config.enabled ? <CheckCircle size="small" /> : <div className="border rounded-circle" style={{ width: '18px', height: '18px' }} />}
                                                </div>
                                                <label className="form-check-label m-0" style={{ cursor: 'pointer' }}>
                                                    {QUESTION_TYPE_LABELS[type].icon}
                                                    <span className="ms-2">{QUESTION_TYPE_LABELS[type].label}</span>
                                                </label>
                                            </div>
                                            <div className="d-flex align-items-center gap-3">
                                                {config.enabled && (
                                                    <Badge bg="primary-subtle" className="text-primary px-3 py-2" style={{ fontWeight: '600' }}>
                                                        {config.count} {config.count === 1 ? 'Question' : 'Questions'} · {config.marks * config.count} Marks
                                                    </Badge>
                                                )}
                                                <Button
                                                    variant={config.enabled ? "outline-danger" : "outline-primary"}
                                                    size="sm"
                                                    className="px-3 d-flex align-items-center gap-1"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleTypeToggle(type);
                                                    }}
                                                    style={{ borderRadius: '8px', fontWeight: '600' }}
                                                >
                                                    {config.enabled ? (
                                                        <><Remove fontSize="small" /> Remove</>
                                                    ) : (
                                                        <><Add fontSize="small" /> Add</>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>

                                        {config.enabled && (
                                            <div className="q-row-body">
                                                <div className="setting-group">
                                                    <div className="setting-item">
                                                        <Form.Label className="fw-bold mb-2">Total Questions</Form.Label>
                                                        <InputGroup size="sm" className="border-0 shadow-none">
                                                            <Button
                                                                variant="light"
                                                                className="border-1 border-end-0"
                                                                onClick={() => decrementConfig(type, 'count', 0)}
                                                                disabled={isGenerating}
                                                                style={{ borderRadius: '8px 0 0 8px' }}
                                                            >
                                                                <RemoveCircleOutline fontSize="small" />
                                                            </Button>
                                                            <Form.Control
                                                                type="number"
                                                                className="text-center border-1 border-start-0 border-end-0"
                                                                value={config.count}
                                                                onChange={(e) => handleConfigChange(type, 'count', e.target.value)}
                                                                disabled={isGenerating}
                                                                style={{ maxWidth: '60px', fontWeight: '600' }}
                                                            />
                                                            <Button
                                                                variant="light"
                                                                className="border-1 border-start-0"
                                                                onClick={() => incrementConfig(type, 'count', 50)}
                                                                disabled={isGenerating}
                                                                style={{ borderRadius: '0 8px 8px 0' }}
                                                            >
                                                                <AddCircleOutline fontSize="small" />
                                                            </Button>
                                                        </InputGroup>
                                                    </div>
                                                    <div className="setting-item">
                                                        <Form.Label className="fw-bold mb-2">Marks per Question</Form.Label>
                                                        <InputGroup size="sm">
                                                            <Button
                                                                variant="light"
                                                                className="border-1 border-end-0"
                                                                onClick={() => decrementConfig(type, 'marks', 1)}
                                                                disabled={isGenerating}
                                                                style={{ borderRadius: '8px 0 0 8px' }}
                                                            >
                                                                <RemoveCircleOutline fontSize="small" />
                                                            </Button>
                                                            <Form.Control
                                                                type="number"
                                                                className="text-center border-1 border-start-0 border-end-0"
                                                                value={config.marks}
                                                                onChange={(e) => handleConfigChange(type, 'marks', e.target.value)}
                                                                disabled={isGenerating}
                                                                style={{ maxWidth: '60px', fontWeight: '600' }}
                                                            />
                                                            <Button
                                                                variant="light"
                                                                className="border-1 border-start-0"
                                                                onClick={() => incrementConfig(type, 'marks', 100)}
                                                                disabled={isGenerating}
                                                                style={{ borderRadius: '0 8px 8px 0' }}
                                                            >
                                                                <AddCircleOutline fontSize="small" />
                                                            </Button>
                                                        </InputGroup>
                                                    </div>
                                                    {/* <div className="setting-item">
                                                        <Form.Label className="fw-bold mb-2">Difficulty Level</Form.Label>
                                                        <Form.Select
                                                            size="sm"
                                                            value={config.difficulty}
                                                            onChange={(e) => handleConfigChange(type, 'difficulty', e.target.value)}
                                                            disabled={isGenerating}
                                                        >
                                                            <option value="easy">Easy</option>
                                                            <option value="medium">Medium</option>
                                                            <option value="hard">Hard</option>
                                                        </Form.Select>
                                                    </div> */}
                                                    <div className="setting-item">
                                                        <Form.Label className="fw-bold mb-2">Bloom's Level</Form.Label>
                                                        <Form.Select
                                                            size="sm"
                                                            value={config.taxonomy}
                                                            onChange={(e) => handleConfigChange(type, 'taxonomy', e.target.value)}
                                                            disabled={isGenerating}
                                                        >
                                                            {BLOOM_TAXONOMY_OPTIONS.map(opt => (
                                                                <option key={opt} value={opt}>{opt}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        {generationError && (
                            <Alert variant="danger" className="mb-4">
                                {generationError}
                            </Alert>
                        )}

                        <div className="d-flex gap-3">
                            <Button
                                variant="secondary"
                                onClick={handleBack}
                                disabled={isGenerating}
                            >
                                <ArrowBack fontSize="small" className="me-1" />
                                Back
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleGenerateQA}
                                disabled={isGenerating || (!selectedResourceFile && !uploadedFileInfo)}
                                className="flex-grow-1"
                            >
                                {isGenerating ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Generating Q&A...
                                    </>
                                ) : (
                                    <>
                                        <PlayArrow fontSize="small" className="me-1" />
                                        Generate Q&A
                                    </>
                                )}
                            </Button>
                        </div>

                        {isGenerating && (
                            <Alert variant="info" className="mt-4">
                                <div className="d-flex align-items-center">
                                    <CircularProgress size={20} className="me-3" />
                                    <div>
                                        <div className="fw-semibold">Q&A Generation in Progress</div>
                                        <small className="text-muted">
                                            Your Q&A is being generated.
                                        </small>
                                    </div>
                                </div>
                            </Alert>
                        )}
                    </>
                )}
                {quizGenerationResponseData && (
                    <Card className="mt-4 shadow-sm">
                        <Card.Header className="bg-primary text-white">
                            <h5 className="mb-0">Generated Q&A – Edit & Save</h5>
                        </Card.Header>
                        <Card.Body>
                            <Alert variant="success" className="mb-4">
                                <strong>{transformedData.length}</strong> questions ready.
                            </Alert>

                            {/* EDITABLE DISPLAY */}
                            <EditableQnADisplay
                                responseData={transformedData}
                                handleGeneratedQuizSubmit={handleGeneratedQuizSubmit}
                                passingPerctage={questionsDetails.passingPercentage}
                                finalAssessment={questionsDetails.finalAssessment}
                                quizTimer={quizTimer}
                            />

                            {/* ORIGINAL PDF BUTTON */}
                            <div className="mt-4 d-flex justify-content-end gap-2">
                                <Button variant="outline-secondary" onClick={handleBack}>
                                    Back
                                </Button>
                                <Button
                                    variant="success"
                                    onClick={() =>
                                        handleGeneratedQuizSubmit(
                                            transformedData,
                                            questionsDetails.passingPercentage,
                                            questionsDetails.finalAssessment,
                                            quizTimer
                                        )
                                    }
                                    disabled={isSavingPDF}
                                >
                                    {isSavingPDF ? "Generating…" : "Save PDF"}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                )}

            </div>
        );
    };

    // Placeholder for viewing generated Q&A (optional future enhancement)
    const renderQAViewer = () => (
        <div className="text-center p-5">
            <CircularProgress />
            <div className="mt-3">Q&A Viewer Coming Soon...</div>
            <Button variant="secondary" onClick={handleBack} className="mt-3">
                <ArrowBack fontSize="small" className="me-1" />
                Back
            </Button>
        </div>
    );

    return (
        <div className="container-fluid">
            <Row>
                <Col>
                    <CommonHeader
                        text={isViewingMode ? "View Q&A" : "Generate Q&A"}
                        subText={isViewingMode ? "Preview your generated Q&A" : "Create AI-powered Q&A from your content"}
                        icon={courseIcon}
                    />
                </Col>
            </Row>

            <Row className="mx-auto">
                <Col sm={isViewingMode ? 12 : 10} md={isViewingMode ? 10 : 8} className="mx-auto pt-5 px-0">
                    <Card>
                        <Card.Body className="p-4">
                            {isViewingMode ? renderQAViewer() : renderGenerationForm()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default QAGeneration;